import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronsDown, FolderKanban, Plus, Search } from 'lucide-react';
import { flattenPages } from '@missions/api-client';
import { Button, useDialogs, useToast } from '@missions/ui';
import { api } from '../../lib/client';
import { useErrorToast, useInfinite, useInvalidate } from '../../lib/hooks';
import { setPageTitle } from '../../lib/seo';
import { useSession, useT } from '../../lib/session';
import { AvStack, Due, Empty } from '../../components/atoms';
import { useChrome, useShell } from '../../layout/shell-context';
import { GoToCard, ProjectTree } from '../../layout/sidebar-parts';

/** All projects in the current workspace (cards with progress rings), cursor-paginated. */
export function ProjectsIndex() {
  const t = useT();
  const s = useSession();
  const shell = useShell();
  const nav = useNavigate();
  const dialogs = useDialogs();
  const toast = useToast();
  const onErr = useErrorToast();
  const invalidate = useInvalidate();
  const [q, setQ] = useState('');
  const list = useInfinite('projects.list', { query: { workspaceId: shell.workspaceId, q: q || undefined, limit: 24 } }, { enabled: !!shell.workspaceId });
  const items = flattenPages(list.data);
  useEffect(() => setPageTitle('Projects'), []);
  useChrome(
    {
      section: 'Projects',
      current: <><FolderKanban style={{ color: 'var(--text-3)' }} /><span className="nm">All projects</span></>,
      secondaryTitle: 'Projects',
      secondary: (
        <>
          <div className="glabel" style={{ paddingTop: 4 }}>All projects</div>
          <ProjectTree />
        </>
      ),
      rail: <GoToCard />,
    },
    [shell.workspaceId],
  );
  const create = async () => {
    const name = await dialogs.prompt({ title: 'New project', label: 'Project name', placeholder: 'e.g. Acme Corp Website Redesign', confirmText: 'Create project', validate: (v) => (v.length < 3 ? 'Use at least 3 characters' : null) });
    if (!name) return;
    try {
      const p = await api.call('projects.create', { body: { workspaceId: shell.workspaceId, name } });
      await invalidate('projects');
      toast(`Project “${p.name}” created`);
      nav(`/p/${p.id}`);
    } catch (e) {
      onErr(e);
    }
  };
  return (
    <div className="page">
      <div className="inbox-h">
        <div>
          <h1>Projects</h1>
          <p>Every client and internal project in this workspace.</p>
        </div>
        <span className="sp" />
        {s.can('project.create') && <Button variant="primary" onClick={() => void create()}><Plus />New project</Button>}
      </div>
      <div className="card">
        <div className="ctrls" style={{ padding: '12px 16px' }}>
          <label className="search">
            <Search />
            <span className="sr">Search projects</span>
            <input type="search" placeholder="Search projects…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>
        {list.isPending ? (
          <div className="missions">
            {[0, 1, 2, 3].map((i) => <div className="mcard" key={i}><span className="ui-skel" style={{ width: '70%', height: 14, display: 'block' }} /><span className="ui-skel" style={{ width: '40%', height: 12, display: 'block' }} /></div>)}
          </div>
        ) : !items.length ? (
          <Empty title={q ? `No projects match “${q}”` : 'No projects yet'} text={`Create a project to start planning ${t.items}.`} />
        ) : (
          <div className="missions">
            {items.map((p) => (
              <button className="mcard" key={p.id} onClick={() => nav(`/p/${p.id}`)}>
                <div className="top">
                  <div className="ms-ring" style={{ ['--p' as string]: p.progress }}><span>{Math.round(p.progress)}%</span></div>
                  <div className="nm">
                    <b data-tooltip={p.name}>{p.name}</b>
                    <small>{p.client?.name ? `${p.client.name} · ` : ''}{p.counts.total} {t.items} · {p.counts.overdue} overdue</small>
                  </div>
                </div>
                <div className="bot">
                  <span className="ms-code">{p.code}</span>
                  <AvStack users={p.members} size={22} max={3} />
                  <Due due={p.dueAt} />
                </div>
              </button>
            ))}
          </div>
        )}
        {list.hasNextPage && (
          <div className="gfoot" style={{ paddingLeft: 16 }}>
            Showing {items.length}
            <Button variant="ghost" className="more" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}><ChevronsDown />Load more</Button>
          </div>
        )}
      </div>
    </div>
  );
}
